import { useId } from 'react';
import { Combobox, ComboboxContent, ComboboxEmpty, ComboboxInput, ComboboxItem, ComboboxList } from '@/components/ui/combobox';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

export type OptionComboboxProps<T> = {
  label: string;
  options: T[];
  value: T | null;
  onChange: (option: T | null) => void;
  getLabel: (option: T) => string;
  isEqual: (option: T, value: T) => boolean;
  placeholder?: string;
  error?: string | undefined;
  disabled?: boolean;
  className?: string;
};

/** A labelled pick-one-of-these field, typing to narrow the list. The legacy Autocomplete's place. */
export function OptionCombobox<T>({
  label,
  options,
  value,
  onChange,
  getLabel,
  isEqual,
  placeholder,
  error,
  disabled = false,
  className,
}: OptionComboboxProps<T>) {
  const id = useId();
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Label htmlFor={id}>{label}</Label>
      <Combobox
        items={options}
        value={value}
        onValueChange={(next) => onChange((next as T | null) ?? null)}
        itemToStringLabel={(option) => (option ? getLabel(option as T) : '')}
        isItemEqualToValue={(option, val) => !!option && !!val && isEqual(option as T, val as T)}
        disabled={disabled}
      >
        <ComboboxInput id={id} placeholder={placeholder} disabled={disabled} aria-invalid={error ? true : undefined} />
        <ComboboxContent>
          <ComboboxList>
            {(option: T) => (
              <ComboboxItem key={getLabel(option)} value={option}>
                {getLabel(option)}
              </ComboboxItem>
            )}
          </ComboboxList>
          <ComboboxEmpty>No matches</ComboboxEmpty>
        </ComboboxContent>
      </Combobox>
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}
